import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import type { BreadcrumbItemData } from '../Breadcrumb/Breadcrumb.types';
import { DrawerHeader } from './DrawerHeader';
import { DrawerSlot } from './shared/DrawerSlot';
import { useDrawerWidthResize } from './shared/useDrawerWidthResize';
import { DrawerToolBar, type DrawerToolBarType } from './DrawerToolBar';
import {
  DEFAULT_DRAWER_STEPS_ITEMS,
  DRAWER_FULL_WIDTH_CSS,
  DRAWER_MIN_WIDTH,
  getDrawerFullWidthPx,
  type DrawerToolbarStepId,
} from './drawerConstants';
import './DrawerItem.css';
import './DrawerHeader.css';
import './shared/drawerShared.css';

/** Inner drawer panel with toolbar, resizable width, header, and content slot. */
export type DrawerItemProps = {
  toolbarType?: DrawerToolBarType;
  fullWidth?: boolean;
  width?: number | string;
  resizable?: boolean;
  divider?: boolean;
  stepsBreadcrumb?: boolean;
  breadcrumbItems?: BreadcrumbItemData[];
  showCloseButton?: boolean;
  showPrimaryButton?: boolean;
  showSubtitle?: boolean;
  title?: string;
  subtitle?: string;
  closeLabel?: string;
  primaryLabel?: string;
  /** Custom actions slot forwarded to DrawerHeader — replaces built-in buttons. */
  actions?: ReactNode;
  slotPadding?: boolean;
  showScrollBar?: boolean;
  showTestInPlayground?: boolean;
  children?: ReactNode;
  className?: string;
  onToolbarClose?: () => void;
  activeToolbarStep?: DrawerToolbarStepId;
  onToolbarStepClick?: (stepId: DrawerToolbarStepId) => void;
  onToolbarWidthToggle?: () => void;
  onToolbarResize?: (width: number) => void;
  onToolbarWidthChange?: (width: number) => void;
  onToolbarTestInPlayground?: () => void;
  onCloseClick?: () => void;
  onPrimaryClick?: () => void;
};

function resolveInitialWidth(
  fullWidth: boolean,
  width: number | string,
  fullWidthPx: number,
): number {
  if (fullWidth) {
    return fullWidthPx;
  }
  if (typeof width === 'number') {
    return width;
  }
  const parsed = Number.parseInt(width, 10);
  return Number.isFinite(parsed) ? parsed : 704;
}

export function DrawerItem({
  toolbarType = 'expand',
  fullWidth = false,
  width = 704,
  resizable = true,
  divider = true,
  stepsBreadcrumb = false,
  breadcrumbItems = DEFAULT_DRAWER_STEPS_ITEMS,
  showCloseButton = false,
  showPrimaryButton = true,
  showSubtitle = true,
  title = 'Long title',
  subtitle = 'Subtitle',
  closeLabel = 'Close',
  primaryLabel = 'Next',
  actions,
  slotPadding = true,
  showScrollBar = false,
  showTestInPlayground = true,
  children,
  className = '',
  onToolbarClose,
  activeToolbarStep,
  onToolbarStepClick,
  onToolbarWidthToggle,
  onToolbarResize,
  onToolbarWidthChange,
  onToolbarTestInPlayground,
  onCloseClick,
  onPrimaryClick,
}: DrawerItemProps) {
  const [fullWidthPx, setFullWidthPx] = useState(() => getDrawerFullWidthPx());

  useEffect(() => {
    const updateFullWidthPx = () => {
      setFullWidthPx(getDrawerFullWidthPx());
    };
    updateFullWidthPx();
    window.addEventListener('resize', updateFullWidthPx);
    return () => {
      window.removeEventListener('resize', updateFullWidthPx);
    };
  }, []);

  // Only recompute the starting width when fullWidth / width props change — not on every
  // viewport resize (that path is handled by maxWidth + stick-to-max in the resize hook).
  const initialWidth = useMemo(
    () => resolveInitialWidth(fullWidth, width, getDrawerFullWidthPx()),
    [fullWidth, width],
  );

  const { width: containerWidth, isResizing, handleResizePointerDown, endResize } =
    useDrawerWidthResize({
      initialWidth,
      minWidth: DRAWER_MIN_WIDTH,
      maxWidth: fullWidthPx,
      onWidthChange: onToolbarWidthChange,
      onResizeEnd: onToolbarResize,
    });

  const containerStyle: CSSProperties = {
    width: `${containerWidth}px`,
    minWidth: `${DRAWER_MIN_WIDTH}px`,
    maxWidth: DRAWER_FULL_WIDTH_CSS,
  };

  const containerClasses = [
    'spark-drawer-item-container',
    'spark-drawer-item-container-fill',
    fullWidth && !resizable ? 'spark-drawer-item-full-width' : '',
    stepsBreadcrumb ? 'spark-drawer-item-container-steps' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const itemClasses = [
    'spark-drawer-item',
    isResizing ? 'spark-drawer-item-resizing' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={itemClasses}>
      <DrawerToolBar
        type={toolbarType}
        activeStep={activeToolbarStep}
        showTestInPlayground={showTestInPlayground}
        isResizing={isResizing}
        onClose={onToolbarClose}
        onStepClick={onToolbarStepClick}
        onWidthToggle={onToolbarWidthToggle}
        onResizePointerDown={resizable ? handleResizePointerDown : undefined}
        onResizePointerEnd={resizable ? endResize : undefined}
        onTestInPlayground={onToolbarTestInPlayground}
      />
      <div className={containerClasses} style={containerStyle}>
        <DrawerHeader
          divider={divider}
          stepsBreadcrumb={stepsBreadcrumb}
          breadcrumbItems={breadcrumbItems}
          showCloseButton={showCloseButton}
          showPrimaryButton={showPrimaryButton}
          showSubtitle={showSubtitle}
          title={title}
          subtitle={subtitle}
          closeLabel={closeLabel}
          primaryLabel={primaryLabel}
          actions={actions}
          onCloseClick={onCloseClick}
          onPrimaryClick={onPrimaryClick}
        />
        <DrawerSlot slotPadding={slotPadding} showScrollBar={showScrollBar}>
          {children}
        </DrawerSlot>
      </div>
    </div>
  );
}
